/**
 * @prettier
 */
import {createSelector} from 'reselect';
import getListingPageModalStates from './modalStates';
import {getSystem, getTranslations, getTripData} from './store';
import {
    getArrivedFromExternalSite,
    getIsMobile,
    getLocaleJS,
    getSiteName,
    getSystemClientSideRendered,
    getBaseUrlPath
} from './system';
import {availableRoutes} from '../routes';
import {getGeography, getPredictedLocation} from './listing';
import {getCalendarLabels} from './availabilityCalendar';
import {isInBucket} from '../utils/abacusHelper';

const getLabels = createSelector(
    [getTranslations, getCalendarLabels],
    (translations, calendarLabels) => {
        return {
            searchMoreProperties: translations.searchMoreProperties,
            mobileTitle: translations.search,
            buttonTitle: translations.search,
            calendarLabels,
            datePicker: {
                label: translations.checkIn,
                label2: translations.checkOut,
                clear: translations.clearDates,
                done: translations.done
            },
            guests: {
                label: translations.searchGuestsLabel,
                adult: translations.adultSingular,
                adults: translations.adultPlural,
                child: translations.childSingular,
                children: translations.childPlural,
                pets: translations.pets,
                guests: translations.guests,
                yesBtn: translations.guestSelectorYes,
                noBtn: translations.guestSelectorNo,
                clearBtn: translations.clear,
                applyBtn: translations.apply,
                modalTitle: translations.selectGuests,
                mobileBtn: translations.continue
            },
            typeahead: {
                destinationplaceholdermsg: translations.searchBy,
                destinationselected: translations.destinationSelected,
                noresultsfound: translations.noResultsFound,
                recentlyviewed: translations.recentlyViewed,
                recentsearches: translations.yourRecentSearches,
                resultsfound: translations.resultsFound,
                where: translations.destination
            }
        };
    }
);

const getCookieLocationString = createSelector(
    [getSystem],
    (system) => {
        const statePersistCookie = system.cookies['ha-state-prst'] || {};
        const keywords = statePersistCookie.lbsKeywords;

        return keywords && keywords.split('+').join(' ');
    }
);

const getLocation = createSelector(
    [getCookieLocationString, getArrivedFromExternalSite, getGeography, getPredictedLocation],
    (cookieLocationString, arrivedFromExternalSite, geography, predictedLocation) => {
        if (!arrivedFromExternalSite && cookieLocationString) {
            return cookieLocationString;
        }
        const geoLocationName = geography && geography.description;
        const predictedLocationName =
            predictedLocation && predictedLocation.name && predictedLocation.name.full;
        const locationPredictionTestOn = isInBucket('ha_pdp_predicted_search_term', 1, true);
        if (locationPredictionTestOn && predictedLocationName) {
            return predictedLocationName;
        } else if (!locationPredictionTestOn && geoLocationName) {
            return geoLocationName;
        }

        return null;
    }
);

const getGuests = createSelector(
    [getTripData],
    (tripData) => {
        return {
            adultsCount: tripData.adults,
            childrenCount: tripData.children,
            petIncluded: tripData.hasPets,
            adultsMin: 1
        };
    }
);

const getDates = createSelector(
    [getTripData],
    (tripData) => {
        return {
            arrive: tripData.arriveDateText,
            depart: tripData.departDateText,
            arrivedWithoutDates: tripData.arrivedWithoutDates
        };
    }
);

const getRoutes = createSelector(
    [getBaseUrlPath],
    (baseUrlPath) => {
        return {
            search: `${baseUrlPath}?r=${availableRoutes.SEARCH}`,
            datePicker: `${baseUrlPath}?r=${availableRoutes.SEARCH_DATE_PICKER}`,
            guestPicker: `${baseUrlPath}?r=${availableRoutes.SEARCH_GUEST_PICKER}`
        };
    }
);

const getHideDates = createSelector(
    [],
    () => false
);

const getHideGuests = createSelector(
    [getIsMobile, getArrivedFromExternalSite],
    (isMobile, arrivedFromExternalSite) => !isMobile || arrivedFromExternalSite
);

const getSearchBoxSelector = createSelector(
    [
        getLabels,
        getListingPageModalStates,
        getLocation,
        getIsMobile,
        getLocaleJS,
        getSiteName,
        getArrivedFromExternalSite,
        getGuests,
        getHideGuests,
        getDates,
        getHideDates,
        getRoutes,
        getSystemClientSideRendered
    ],
    (
        labels,
        modalStates,
        location,
        isMobile,
        localeJS,
        systemSiteName,
        arrivedFromExternalSite,
        guests,
        hideGuests,
        dates,
        hideDates,
        routes,
        clientSideRendered
    ) => {
        return {
            clientSideRendered,
            isMobile,
            labels,
            location,
            localeJS,
            siteName: systemSiteName.toLowerCase(),
            arrivedFromExternalSite,
            guests,
            hideGuests,
            checkIn: dates.arrive,
            checkOut: dates.depart,
            arrivedWithoutDates: dates.arrivedWithoutDates,
            hideDates,
            searchRoute: routes.search,
            datePickerRoute: routes.datePicker,
            guestPickerRoute: routes.guestPicker,
            isSearchModalOpen: modalStates.searchModalOpen,
            isSearchDatePickerOpen: modalStates.searchDatePickerOpen,
            isSearchGuestPickerOpen: modalStates.searchGuestPickerOpen
        };
    }
);

export default getSearchBoxSelector;
export {
    getLabels,
    getLocation,
    getCookieLocationString,
    getSearchBoxSelector,
    getGuests,
    getDates,
    getRoutes,
    getHideDates,
    getHideGuests
};
